import { useEffect, useMemo, useState } from "react"

import type {
    IIndexedValidationError,
    IPagination,
} from "@common-shared/types/general.types"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types"

import { POLICIES_CONFIG } from "../config/policies.config"

/**
 * Manage a client-side review list, its validation errors, and persisted pagination.
 */
export const useClientPaginatedReview = <TItem>(
    items: TItem[],
    errors: IIndexedValidationError[],
    storageKey: string,
) => {
    const [reviewStart, setReviewStart] = useState(0)

    const [selectedPaginationOption, setSelectedPaginationOption] =
        useState<ISelectRenderedOption>(
            BrowserStorageHelper.get<ISelectRenderedOption>(
                storageKey,
                undefined,
                BrowserStorageType.sessionStorage,
            ) ?? POLICIES_CONFIG.availablePaginationOptions[0],
        )

    const reviewCount = Number(selectedPaginationOption.value)
    const reviewTotal = items.length

    // Start of the last page, not the last item offset: clamping to
    // total - count lands between pages and desyncs the page indicator
    const maxReviewStart = useMemo<number>(() => {
        return (
            Math.max(0, Math.ceil(reviewTotal / reviewCount) - 1) * reviewCount
        )
    }, [reviewTotal, reviewCount])

    const reviewClampedStart = useMemo<number>(() => {
        return Math.min(reviewStart, maxReviewStart)
    }, [reviewStart, maxReviewStart])

    const errorByIndex = useMemo(() => {
        return new Map(errors.map(error => [error.index, error]))
    }, [errors])

    // Items that have no validation errors
    const validItems = useMemo(() => {
        return items.filter((_, index) => !errorByIndex.has(index))
    }, [items, errorByIndex])

    const pageItems = useMemo(() => {
        return items.slice(reviewClampedStart, reviewClampedStart + reviewCount)
    }, [items, reviewClampedStart, reviewCount])

    useEffect(() => {
        BrowserStorageHelper.set(
            storageKey,
            selectedPaginationOption,
            BrowserStorageType.sessionStorage,
        )
    }, [selectedPaginationOption, storageKey])

    // Reset the review start to the first page when the pagination option changes
    useEffect(() => {
        setReviewStart(0)
    }, [selectedPaginationOption])

    useEffect(() => {
        if (reviewStart > maxReviewStart) {
            setReviewStart(maxReviewStart)
        }
    }, [maxReviewStart, reviewStart])

    const nextPagination = useMemo<IPagination>(() => {
        const start = reviewClampedStart + reviewCount

        return {
            start,
            count: reviewCount,
            total: reviewTotal,
            moreAvailable: start < reviewTotal,
        }
    }, [reviewClampedStart, reviewCount, reviewTotal])

    /**
     * Move to the page requested by the shared pagination control.
     */
    const getNewItems = (pagination: IPagination) => {
        setReviewStart(pagination.start)
    }

    /**
     * Return to the first page after replacing the review data.
     */
    const resetReviewStart = () => {
        setReviewStart(0)
    }

    return {
        errorByIndex,
        getNewItems,
        nextPagination,
        pageItems,
        resetReviewStart,
        reviewClampedStart,
        reviewTotal,
        selectedPaginationOption,
        setSelectedPaginationOption,
        validItems,
    }
}
